import type { ApolloCache } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { cardEditContext } from '@context/card-edit-context';
import { CardPropertyDetailsFragmentDoc } from '@graphql/fragments/cards/property.graphql';
import {
  SetCardNamePropertiesDocument,
  type SetCardNamePropertiesMutation,
} from '@graphql/mutations/cards/property.graphql';
import type { CardNameFields } from '@hooks/use-card-names-state';
import { cardClaimContext } from 'app-next/claim/card/contexts';
import { useContext } from 'react';

import { CardFlavours } from '../types';
import type { NameFields } from './types';
import type { SetCardPropertiesInput, ValueProvider } from '.cache/__types__';

const composeOptimisticResponse = ({
  claimOrUpdateFlow,
  cardId,
  state,
  fields,
  action,
}: {
  claimOrUpdateFlow: boolean;
  cardId: string;
  state: CardNameFields;
  fields: NameFields;
  action: 'update' | 'delete';
}): SetCardNamePropertiesMutation => {
  const baseProperty = {
    card_id: cardId,
    instruction_text: null,
    template_id: null,
    template: null,
    value_locked: false,
    is_optional: false,
    stage: null,
    value_provider: null,
    attribute_type: null,
  };

  return {
    __typename: 'Mutation',
    setCardProperties: {
      __typename: 'CardProperties',
      honorific_prefix: {
        __typename: 'CardProperty',
        id: fields.honorific_prefix?.id || 'temp-honorific-prefix-id',
        value: action === 'update' ? state.honorificPrefix : null,
        ...baseProperty,
      },
      first_name: {
        __typename: 'CardProperty',
        id: fields.first_name?.id || 'temp-first-name-id',
        value: action === 'update' ? state.firstName : null,
        ...baseProperty,
        stage: claimOrUpdateFlow && state.firstName ? 'done' : 'todo',
        attribute_type: fields.first_name?.attribute_type || null,
        // Only set value provider on first_name because that is our source of truth for the whole name section
        value_provider: fields.first_name?.value_provider ?? null,
      },
      preferred_name: {
        __typename: 'CardProperty',
        id: fields.preferred_name?.id || 'temp-preferred-name-id',
        value: action === 'update' ? state.preferredName : null,
        ...baseProperty,
      },
      middle_name: {
        __typename: 'CardProperty',
        id: fields.middle_name?.id || 'temp-middle-name-id',
        value: action === 'update' ? state.middleName : null,
        ...baseProperty,
      },
      last_name: {
        __typename: 'CardProperty',
        id: fields.last_name?.id || 'temp-last-name-id',
        value: action === 'update' ? state.lastName : null,
        ...baseProperty,
      },
      honorific_suffix: {
        __typename: 'CardProperty',
        id: fields.honorific_suffix?.id || 'temp-honorific-suffix-id',
        value: action === 'update' ? state.honorificSuffix : null,
        ...baseProperty,
      },
      maiden_name: {
        __typename: 'CardProperty',
        id: fields.maiden_name?.id || 'temp-maiden-name-id',
        value: action === 'update' ? state.maidenName : null,
        ...baseProperty,
      },
      pronouns: {
        __typename: 'CardProperty',
        id: fields.pronouns?.id || 'temp-pronouns-id',
        value: action === 'update' ? state.pronouns : null,
        ...baseProperty,
      },
    },
  };
};

const composeMutationInput = ({
  claimOrUpdateFlow,
  action,
  cardId,
  state,
}: {
  claimOrUpdateFlow: boolean;
  action: 'update' | 'delete';
  cardId: string;
  state: CardNameFields;
}): SetCardPropertiesInput => {
  return {
    card_id: cardId,
    honorific_prefix: {
      value: action === 'update' ? state.honorificPrefix : null,
      value_provider: action === 'update' ? (state.valueProvider ?? null) : null,
    },
    first_name: {
      value: action === 'update' ? state.firstName : null,
      stage: action === 'update' && claimOrUpdateFlow && state.firstName ? 'done' : 'todo',
      // Only set value provider on first_name because that is our source of truth for the whole name section, same for value_locked
      value_provider: action === 'update' ? (state.valueProvider ?? null) : null,
      value_locked: action === 'update' ? state.locked : false,
    },
    preferred_name: {
      value: action === 'update' ? state.preferredName : null,
      value_provider: action === 'update' ? (state.valueProvider ?? null) : null,
    },
    middle_name: {
      value: action === 'update' ? state.middleName : null,
      value_provider: action === 'update' ? (state.valueProvider ?? null) : null,
    },
    last_name: {
      value: action === 'update' ? state.lastName : null,
      value_provider: action === 'update' ? (state.valueProvider ?? null) : null,
    },
    honorific_suffix: {
      value: action === 'update' ? state.honorificSuffix : null,
      value_provider: action === 'update' ? (state.valueProvider ?? null) : null,
    },
    maiden_name: {
      value: action === 'update' ? state.maidenName : null,
      value_provider: action === 'update' ? (state.valueProvider ?? null) : null,
    },
    pronouns: {
      value: action === 'update' ? state.pronouns : null,
      value_provider: action === 'update' ? (state.valueProvider ?? null) : null,
    },
  };
};

const createFragmentRefs = (cache: ApolloCache, data: SetCardNamePropertiesMutation | null | undefined) => {
  const propertyEntries = Object.entries(data?.setCardProperties || {}).filter(
    (item) => item[0] !== '__typename' || !item,
  );
  const propertyFragmentRefs = {};

  const fragRefs = propertyEntries.reduce((acc, curr) => {
    const [propertyName, property] = curr;

    if (!property || typeof property === 'string') {
      return acc;
    }

    const newCardPropertyRef = cache.writeFragment({
      id: `CardProperty:${property.id}`,
      data: property,
      fragment: CardPropertyDetailsFragmentDoc,
      fragmentName: 'cardPropertyDetails',
    });

    return {
      ...acc,
      [propertyName]: newCardPropertyRef,
    };
  }, propertyFragmentRefs);

  return fragRefs;
};

export const useCardNameMutation = ({
  cardId,
  state,
  fields,
}: {
  cardId: string;
  state: CardNameFields;
  fields: NameFields;
}): {
  delete: () => void;
  update: (newValueProvider?: ValueProvider) => void;
} => {
  const [setProperties] = useMutation(SetCardNamePropertiesDocument);

  const { claimCardToken } = useContext(cardClaimContext);

  const { flavour } = useContext(cardEditContext);

  const claimOrUpdateFlow =
    flavour === CardFlavours.CLAIM ||
    flavour === CardFlavours.ADD_DETAILS ||
    flavour === CardFlavours.INVITE_TOKEN_CLAIM;

  const mutationContext = claimCardToken
    ? {
        headers: {
          authorization: `ClaimCard ${claimCardToken}`,
        },
      }
    : undefined;

  const setNameProperties = (action: 'update' | 'delete', newValueProvider?: ValueProvider) => {
    if (action === 'update' && newValueProvider) {
      state.valueProvider = newValueProvider;
    }

    setProperties({
      context: mutationContext,
      variables: {
        input: {
          ...composeMutationInput({
            cardId,
            state,
            action,
            claimOrUpdateFlow,
          }),
          update_profile: flavour === CardFlavours.INVITE_TOKEN_CLAIM,
        },
      },
      optimisticResponse: composeOptimisticResponse({
        claimOrUpdateFlow,
        cardId,
        action,
        fields,
        state,
      }),
      update: (cache, { data }) => {
        const refs = createFragmentRefs(cache, data);
        cache.modify({
          id: cache.identify({ __typename: 'Card', id: cardId }),
          fields: {
            properties(preExistingProperties) {
              return {
                ...preExistingProperties,
                ...refs,
              };
            },
          },
        });
      },
    });
  };

  return {
    delete: () => setNameProperties('delete'),
    update: (newValueProvider?: ValueProvider) => setNameProperties('update', newValueProvider),
  };
};
